import React, { useMemo } from 'react'
import { AvatarWithoutDot } from '@components/Avatar/Avatar'
import { useLiveSpaceState } from '@components/Live/LiveClientProvider'
import { NoMatches } from '@components/NoMatches/NoMatches'
import { Panel } from '@components/Panel/Panel'
import { isModeratorPermission } from '@components/Web3/utils'
import { Box, Icon, Paragraph, Stack, TextField } from '@ui'
import { useFuzzySearchByProperty } from 'hooks/useFuzzySearchByProperty'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import {
    useContractSpaceInfo,
    useMultipleRoleDetails,
    useOfflineStore,
    useRoles,
    useSpaceData,
    useSpaceId,
    useSpaceMembers,
    useUserLookupArray,
} from 'use-towns-client'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'

type MemberInfo = {
    userId: string
    name: string
    isBot: boolean | undefined
    isOnline: boolean
    role: 'owner' | 'admin' | 'member'
}

export const TownMembersPanel = () => {
    const { memberIds } = useSpaceMembers()
    const space = useSpaceData()

    const panelLabel = space?.name ? `${space.name} Members` : 'Town Members'

    return (
        <Panel label={panelLabel} padding="none">
            <TownMembers memberIds={memberIds} />
        </Panel>
    )
}

const TownMembers = (props: { memberIds: string[] }) => {
    const spaceId = useSpaceId()

    const lookupUsers = useUserLookupArray(props.memberIds, spaceId)

    // Get online user IDs from live state
    const liveSpaceState = useLiveSpaceState(spaceId ?? '')
    const onlineUserIds = useMemo(() => {
        const onlineIds = liveSpaceState?.subscribers.userIds ?? []
        return new Set(onlineIds)
    }, [liveSpaceState?.subscribers.userIds])

    // Get role information
    const { data: contractSpaceInfo } = useContractSpaceInfo(spaceId)
    const owner = contractSpaceInfo?.owner

    const { spaceRoles } = useRoles(spaceId ?? '')

    const { data: roles } = useMultipleRoleDetails(
        spaceId ?? '',
        spaceRoles?.map((role) => role.roleId) ?? [],
    )

    const moderatorRoles = useMemo(() => {
        return roles?.filter((role) =>
            role.permissions.some((permission) => isModeratorPermission(permission)),
        )
    }, [roles])

    const { offlineWalletAddressMap } = useOfflineStore()

    const membersWithNames = useMemo((): MemberInfo[] => {
        return lookupUsers.map((user) => {
            const abstractAccount = offlineWalletAddressMap[user.userId]
            let role: 'owner' | 'admin' | 'member' = 'member'

            if (abstractAccount && abstractAccount === owner) {
                role = 'owner'
            } else if (
                abstractAccount &&
                moderatorRoles?.some((r) => r.users.includes(abstractAccount))
            ) {
                role = 'admin'
            }

            return {
                userId: user.userId,
                name: getPrettyDisplayName(user),
                isBot: user.isBot,
                isOnline: onlineUserIds.has(user.userId),
                role,
            }
        })
    }, [lookupUsers, onlineUserIds, offlineWalletAddressMap, owner, moderatorRoles])

    const { searchText, filteredItems, handleSearchChange } =
        useFuzzySearchByProperty(membersWithNames)

    // Split into online and role-based groups
    const groups = useMemo(() => {
        const online: MemberInfo[] = []
        const owners: MemberInfo[] = []
        const admins: MemberInfo[] = []
        const members: MemberInfo[] = []

        for (const user of filteredItems) {
            if (user.isOnline) {
                online.push(user)
            } else {
                // Offline users go into role-based groups
                switch (user.role) {
                    case 'owner':
                        owners.push(user)
                        break
                    case 'admin':
                        admins.push(user)
                        break
                    default:
                        members.push(user)
                }
            }
        }

        return { online, owners, admins, members }
    }, [filteredItems])

    const { openPanel } = usePanelActions()

    const onOpenProfile = (profileId: string) => {
        openPanel('profile', { profileId })
    }

    return (
        <Stack padding minHeight="forceScroll" gap="md">
            <Box position="sticky" top="md" zIndex="above">
                <TextField
                    background="level2"
                    placeholder="Search members"
                    value={searchText}
                    onChange={handleSearchChange}
                />
            </Box>
            <Box grow gap>
                {/* Online section */}
                {groups.online.length > 0 && (
                    <Stack gap="md">
                        <Paragraph color="gray2">Online</Paragraph>
                        <Stack gap="sm">
                            {groups.online.map((user) => (
                                <MemberRow
                                    isOnline
                                    key={user.userId}
                                    userId={user.userId}
                                    name={user.name}
                                    isBot={user.isBot}
                                    onClick={() => onOpenProfile(user.userId)}
                                />
                            ))}
                        </Stack>
                    </Stack>
                )}

                {/* Founder section */}
                {groups.owners.length > 0 && (
                    <Stack gap="md">
                        <Paragraph color="rainbow" alignSelf="start">
                            Founder
                        </Paragraph>
                        <Stack gap="sm">
                            {groups.owners.map((user) => (
                                <MemberRow
                                    key={user.userId}
                                    userId={user.userId}
                                    name={user.name}
                                    isBot={user.isBot}
                                    isOnline={false}
                                    onClick={() => onOpenProfile(user.userId)}
                                />
                            ))}
                        </Stack>
                    </Stack>
                )}

                {/* Admin section */}
                {groups.admins.length > 0 && (
                    <Stack gap="md">
                        <Paragraph color="cta2">Admin</Paragraph>
                        <Stack gap="sm">
                            {groups.admins.map((user) => (
                                <MemberRow
                                    key={user.userId}
                                    userId={user.userId}
                                    name={user.name}
                                    isBot={user.isBot}
                                    isOnline={false}
                                    onClick={() => onOpenProfile(user.userId)}
                                />
                            ))}
                        </Stack>
                    </Stack>
                )}

                {/* Everyone section */}
                {groups.members.length > 0 && (
                    <Stack gap="md">
                        <Paragraph color="gray2">Everyone</Paragraph>
                        <Stack gap="sm">
                            {groups.members.map((user) => (
                                <MemberRow
                                    key={user.userId}
                                    userId={user.userId}
                                    name={user.name}
                                    isBot={user.isBot}
                                    isOnline={false}
                                    onClick={() => onOpenProfile(user.userId)}
                                />
                            ))}
                        </Stack>
                    </Stack>
                )}

                {filteredItems.length === 0 && <NoMatches searchTerm={searchText} />}
            </Box>
        </Stack>
    )
}

const MemberRow = React.memo(function MemberRow(props: {
    userId: string
    name: string
    isBot: boolean | undefined
    isOnline: boolean
    onClick: () => void
}) {
    const { userId, name, isBot, isOnline, onClick } = props

    return (
        <Stack
            horizontal
            hoverable
            alignItems="center"
            borderRadius="xs"
            background="level1"
            gap="sm"
            padding="sm"
            insetX="xs"
            insetY="xxs"
            cursor="pointer"
            onClick={onClick}
        >
            <Box position="relative">
                <AvatarWithoutDot lazy userId={userId} size="avatar_x4" dot={isOnline} />
            </Box>
            <Stack horizontal grow alignItems="center" gap="sm">
                <Paragraph>{name}</Paragraph>
                {isBot && <Icon type="robot" size="square_xs" color="gray2" />}
            </Stack>
        </Stack>
    )
})
